import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { PanelLeftClose, PanelRightClose, Search, Bell } from "lucide-react";
import { Button } from "@/components/ui/button";
import ModeToggle from "@/components/layout/ModeToggle";
import ProfileDropdown from "@/components/layout/ProfileDropdown";
import { useToast } from "@/components/ui/use-toast";
import request from "@/services/api";

interface IProps {
  setSideNavOpen: (state: boolean) => void;
  isSideNavOpen: boolean;
}

const Header = ({ setSideNavOpen, isSideNavOpen }: IProps) => {
  const { toast } = useToast();
  const [unreadCount, setUnreadCount] = useState<number>(0);

  // Fetch unread count
  useEffect(() => {
    const fetchUnreadCount = async () => {
      try {
        const res = await request.get('/api/v1/notification/unread-count');
        if (res.data?.count !== undefined) {
          setUnreadCount(res.data.count);
        }
      } catch (e) {
        // silently ignore if guest
      }
    };
    fetchUnreadCount();
  }, []);

  // SSE Stream Listener
  useEffect(() => {
    const rawApiUrl = process.env.REACT_APP_API_BASE_URL || 'https://dev-api.01maktab.uz';
    const cleanUrl = rawApiUrl.replace(/\/api\/v1\/?$/, '').replace(/\/+$/, '');
    const sseUrl = `${cleanUrl}/api/v1/notification/stream`;

    let eventSource: EventSource | null = null;
    try {
      eventSource = new EventSource(sseUrl);

      eventSource.onmessage = (event) => {
        try {
          const payload = JSON.parse(event.data);
          const notification = payload.data;
          if (notification) {
            toast({
              title: notification.title || 'Yangi bildirishnoma!',
              description: notification.description || 'Yangi bildirishnoma qabul qilindi',
            });
            setUnreadCount((prev) => prev + 1);
          }
        } catch (err) {
          // ignore parse errors
        }
      };

      eventSource.onerror = () => {
        // SSE auto-reconnects
      };
    } catch (e) {
      // ignore
    }

    return () => {
      eventSource?.close();
    };
  }, [toast]);

  return (
    <header className="sticky top-0 z-50 border-b border-border bg-card text-card-foreground h-[72px] flex items-center px-6 transition-all duration-300 shadow-sm">
      <div className="flex items-center justify-between w-full">
        <div className="flex items-center gap-4 flex-1">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSideNavOpen(!isSideNavOpen)}
            className="hover:bg-muted text-muted-foreground hover:text-foreground rounded-xl"
          >
            {isSideNavOpen ? <PanelLeftClose className="size-5" /> : <PanelRightClose className="size-5" />}
          </Button>

          <div className="hidden md:flex items-center relative w-full max-w-sm">
            <Search className="absolute left-3 size-4 text-muted-foreground" />
            <input
              type="text"
              placeholder="Qidirish..."
              className="w-full bg-muted/50 border border-border rounded-xl outline-none pl-10 pr-4 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:bg-card focus:border-primary transition-colors"
            />
          </div>
        </div>

        <div className="flex items-center gap-3">
          <Link to="/notifications">
            <Button variant="ghost" size="icon" className="text-muted-foreground hover:text-foreground hover:bg-muted relative rounded-xl">
              <Bell className="size-5" />
              {unreadCount > 0 && (
                <span className="absolute top-1.5 right-1.5 flex size-4 items-center justify-center rounded-full bg-primary ring-2 ring-card text-[9px] font-bold text-white">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </Button>
          </Link>

          <div className="h-6 w-[1px] bg-border mx-2 hidden sm:block"></div>

          <ModeToggle />
          <ProfileDropdown />
        </div>
      </div>
    </header>
  );
};

export default Header;
